import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { PrintLayout, usePrintData } from '../../components/PrintLayout';
import { useAuth } from '../../context/AuthContext';
import { getErrorMessage } from '../../utils/apiError';
import { formatSignedAt } from '../../utils/technician';
import { canTickJobChecklist } from '../../utils/permissions';
import { ServiceOrderDocument } from './ServiceOrderDocument';

interface JobPrintData {
  job: types.Job;
  company: types.CompanySettings;
  checklist: types.JobChecklist | null;
  /** Why a checklist the job has is not printed (screen only); null when it is printed or there is none */
  checklistNote: string | null;
  /** The checklist request failed (retry may help) */
  checklistFailed: boolean;
  /** The job's current customer signature (item 42); null when none or it could not be read */
  signature: { record: types.JobSignature; imageUrl: string } | null;
}

/** A data URL, so the image is in the page (not fetched later) when printing starts. */
function blobDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function SignatureBlock({ signature, timeZone }: { signature: NonNullable<JobPrintData['signature']>; timeZone?: string }) {
  return (
    <section className="print-keep mt-8" aria-label="Captured signature" data-testid="print-signature">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Signed on site</h2>
      <img src={signature.imageUrl} alt={`Signature of ${signature.record.signerName}`} className="mt-2 h-24 max-w-full object-contain" />
      <p className="mt-1 text-sm">{signature.record.signerName} - {formatSignedAt(signature.record.signedAt, timeZone)}</p>
    </section>
  );
}

/**
 * /print/jobs/:id — the service order (job ticket) a technician takes to the
 * property. Uses only what GET /jobs/:id embeds (customer, property,
 * assignee): technicians may not be allowed the customer or user endpoints,
 * and a valid ticket must not fail because of them. Opening or printing it
 * never changes the Job's status (bulk work orders confirm separately,
 * tracker item 58).
 *
 * The checklist (tracker item 44) is fetched only when the job response says
 * it has steps and the viewer may read it (office, or the assigned
 * technician). A checklist failure never blocks the ticket: it prints without
 * the block and a screen-only note says so. The current customer signature
 * (image, signer name and time) is printed the same way, and a failure to
 * read it is ignored silently.
 */
export function JobPrintPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo(() => createAPIClient(), []);
  const { user } = useAuth();
  const state = usePrintData<JobPrintData>(id, async () => {
    const [job, company] = await Promise.all([api.getJob(id), api.getCompanySettings()]);
    let checklist: types.JobChecklist | null = null;
    let checklistNote: string | null = null;
    let checklistFailed = false;
    if ((job.checklist?.total ?? 0) > 0) {
      if (!canTickJobChecklist(user, job)) {
        checklistNote = 'This job has a checklist, but only office staff and the assigned technician can see it, so it is not printed.';
      } else {
        try {
          checklist = await api.getJobChecklist(id);
        } catch (err) {
          checklistFailed = true;
          checklistNote = `Checklist not loaded: ${getErrorMessage(err, 'request failed')} This service order prints without its checklist; retry to include it.`;
        }
      }
    }
    // The customer signature (item 42): office and the assigned technician may read it. Any failure is
    // ignored on purpose, so printing the ticket never breaks because of it.
    let signature: JobPrintData['signature'] = null;
    if (canTickJobChecklist(user, job)) {
      try {
        const list = await api.listTechnicianSignatures(id);
        if (list.current) {
          const imageUrl = await blobDataUrl(await api.downloadTechnicianSignatureImage(id, list.current.id));
          if (imageUrl) signature = { record: list.current, imageUrl };
        }
      } catch {
        signature = null;
      }
    }
    return { job, company, checklist, checklistNote, checklistFailed, signature };
  }, 'Failed to load job');
  const job = state.data?.job;
  const title = job ? `Service order - ${job.customer.name} - ${job.property.address}` : 'Service order';

  return (
    <PrintLayout title={title} backTo={`/jobs/${id}`} backLabel="Back to job" noun="service order" state={state} company={state.data?.company ?? null}>
      {state.data?.checklistNote && (
        <p role="alert" className="print:hidden mb-6 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-checklist-note>
          {state.data.checklistNote}
          {state.data.checklistFailed && <>{' '}<button type="button" className="text-blue-700 underline" onClick={state.retry}>Retry</button></>}
        </p>
      )}
      {job && <ServiceOrderDocument job={job} checklist={state.data?.checklist ?? null} />}
      {state.data?.signature && <SignatureBlock signature={state.data.signature} timeZone={state.data.company.timezone} />}
    </PrintLayout>
  );
}
